import React, { useEffect, useState, useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import { MOCK_MODE } from '../config/mock';

// Mock IPC descriptions for demo
const MOCK_IPC_DESCS: Record<string, string> = {
  G06F: '電気的デジタルデータ処理',
  G01S: '無線による位置測定',
  H04W: '無線通信ネットワーク',
  B64C: '飛行機・ヘリコプター',
  G08G: '交通制御システム',
  H04L: 'デジタル情報の伝送',
  G05D: '非電気的変量の制御',
  G01C: '距離・水準・方位の測定',
  'H02J50/90': '位置検出や位置最適化を含むもの',
  'H02J50/00': 'ワイヤレスで電力給電または電力配電を行うための回路装置',
  'B64U70/97': '無人航空機の位置制御',
  'B60L53/12': '電気自動車の充電ステーション',
  'G06Q10/083': '物流・配送の管理',
};

// Cache IPC descriptions globally to avoid repeated API calls
const ipcCache: Record<string, string> = {};

async function fetchIpcDescs(codes: string[]): Promise<Record<string, string>> {
  if (MOCK_MODE) {
    const result: Record<string, string> = {};
    for (const c of codes) {
      if (MOCK_IPC_DESCS[c]) result[c] = MOCK_IPC_DESCS[c];
    }
    return result;
  }

  const uncached = codes.filter((c) => !(c in ipcCache));
  if (uncached.length > 0) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/ipc/desc?codes=${encodeURIComponent(uncached.join(','))}`
      );
      if (res.ok) {
        const data = await res.json();
        for (const [k, v] of Object.entries(data)) {
          ipcCache[k] = v as string;
        }
      }
    } catch {
      // ignore
    }
  }
  const result: Record<string, string> = {};
  for (const c of codes) {
    if (ipcCache[c]) result[c] = ipcCache[c];
  }
  return result;
}

interface IpcTooltipTextProps {
  text: string;
}

/**
 * Renders formula text with IPC/CPC codes wrapped in tooltip spans.
 * Hovering over a code shows its Japanese description.
 */
const IpcTooltipText: React.FC<IpcTooltipTextProps> = ({ text }) => {
  const [descs, setDescs] = useState<Record<string, string>>({});
  const fetchedRef = useRef(false);

  // Extract CPC/IPC codes from text
  const codePattern = /(?:CPC|IPC)=([A-Z]\d{2}[A-Z][\d/]*)/g;
  const codes: string[] = [];
  let m;
  while ((m = codePattern.exec(text)) !== null) {
    if (!codes.includes(m[1])) codes.push(m[1]);
  }

  useEffect(() => {
    if (codes.length === 0 || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchIpcDescs(codes).then(setDescs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  if (codes.length === 0) {
    return <>{text}</>;
  }

  // Split text by CPC/IPC codes and wrap them with tooltips
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const fullPattern = /((?:CPC|IPC)=)([A-Z]\d{2}[A-Z][\d/]*)/g;
  let match;
  let key = 0;

  while ((match = fullPattern.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }

    const prefix = match[1]; // "CPC=" or "IPC="
    const code = match[2];
    const desc = descs[code];

    parts.push(
      <span
        key={key++}
        title={desc ? `${code}: ${desc}` : code}
        style={{
          cursor: desc ? 'help' : 'default',
          borderBottom: desc ? '1px dotted #6b7280' : 'none',
          position: 'relative',
        }}
      >
        {prefix}
        {code}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }

  return <>{parts}</>;
};

export default IpcTooltipText;
